import { Paper, Stack } from "@mui/material";
import { DropdownSingleSelect } from "components/DropdownSingleSelect";
import { GeneralErrorBoundary } from "components/ErrorBoundary";
import { FormRowLabel } from "components/FormRowLabel";
import { TextFieldInput } from "components/Input";
import { YesNoToggleRow } from "components/YesNoToggle";
import { useExecution } from "features/dt-plus-setup/api/hooks/useExecution";
import { FileUploadRow } from "features/shared/components/FileUploadRow";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components/ButtonNew";
import {
  DEFAULT_MESHING_METHOD,
  EXECUTION_ACCEPTED_FORMAT,
  EXECUTION_MESHING_METHODS,
  IS_RT_CONFIG_ENABLED,
} from "../../lib/configs";
import { InlineFilePicker } from "./InlineFilePicker";

export const RunExecutionCard = ({ className }: { className?: string }) => {
  const { t } = useTranslation();

  const { handlers, states } = useExecution(EXECUTION_ACCEPTED_FORMAT.maxSizeMB);
  const { cancelRun, handleFileChange, onRunClick, updateForm } = handlers;
  const { form, isRunning, uploadProgress } = states;

  const acceptedFormatHint = t("Execution.run.acceptedFormat", {
    formats: EXECUTION_ACCEPTED_FORMAT.extensions.join(", "),
  });
  const acceptedSizeHint = EXECUTION_ACCEPTED_FORMAT.maxSizeMB
    ? t("Execution.run.acceptedSize", { max: EXECUTION_ACCEPTED_FORMAT.maxSizeMB })
    : "";

  return (
    <GeneralErrorBoundary>
      <Paper className={className}>
        <h3 className="font-ds-headline-lg">{t("Execution.run.title")}</h3>
        <p className="mt-1 text-lds-foreground-muted font-ds-body-regular-md">{t("Execution.run.description")}</p>
        <Stack className="mt-4 items-start gap-4">
          <Stack className="w-1/2 flex-col items-start gap-2">
            <FormRowLabel label={t("Execution.run.fileInputLabel")} />
            <InlineFilePicker
              acceptedExtensions={EXECUTION_ACCEPTED_FORMAT.extensions}
              className=""
              disabled={isRunning}
              file={form.file}
              onFileChange={handleFileChange}
            />
            <p className="text-lds-foreground-muted font-ds-body-regular-xs">
              {acceptedFormatHint} {acceptedSizeHint}
            </p>
            {isRunning && uploadProgress != null && (
              <FileUploadRow
                indicatorWidthClassName="w-3/5"
                showPercentage
                step={{ name: t("general.uploadingFile"), percentage: uploadProgress, status: "progress" }}
              />
            )}
            <Stack className="mt-4 flex-row flex-wrap items-center gap-3">
              <Button
                className="capitalize"
                disabled={!form.file}
                isLoading={isRunning}
                onClick={onRunClick}
                size="md"
                startIconName="Upload"
              >
                {form.executeRaytracing ? t("Execution.run.uploadAndRun") : t("general.uploadFile")}
              </Button>
              {isRunning && cancelRun && (
                <Button className="capitalize" onClick={cancelRun} size="md" variant="secondary">
                  {t("general.cancel")}
                </Button>
              )}
            </Stack>
          </Stack>
          <Stack className="w-1/2 flex-col gap-4">
            <YesNoToggleRow
              className="w-full"
              onChange={(executeRaytracing) => updateForm({ executeRaytracing })}
              question={t("Execution.run.executeRaytracingQuestion")}
              title={t("Execution.run.executeRaytracingTitle")}
              value={form.executeRaytracing}
            >
              {IS_RT_CONFIG_ENABLED && form.executeRaytracing && (
                <Stack className="w-full items-start gap-4">
                  <Stack className="w-full flex-col items-start gap-2">
                    <span className="font-ds-body-bold-md">{t("Execution.run.meshingMethodLabel")}</span>
                    <DropdownSingleSelect
                      className="w-full"
                      clearable={false}
                      hideLabel
                      options={[...EXECUTION_MESHING_METHODS]}
                      selectedValue={form.meshingMethod}
                      setSelectedValue={(value) => updateForm({ meshingMethod: value ?? DEFAULT_MESHING_METHOD })}
                    />
                  </Stack>
                  <TextFieldInput
                    height="4rem"
                    label={t("Execution.run.sensorOffsetLabel")}
                    onChange={(event) => updateForm({ sensorOffsetF: Number(event.target.value) })}
                    type="number"
                    value={form.sensorOffsetF}
                  />
                </Stack>
              )}
            </YesNoToggleRow>
          </Stack>
        </Stack>
      </Paper>
    </GeneralErrorBoundary>
  );
};
